import { useQuery } from '@apollo/react-hooks';
import useGlobalNotifications from '@peachjar/ui/dist/lib/hooks/useGlobalNotifications';
import gql from 'graphql-tag';
import idx from 'idx';
import * as React from 'react';
import portalBFFClient from '../_app/apollo/portalBFF.apolloClient';
import config from '../config';

const SYNC_WARNING_THRESHOLD_MONTHS = 6;

export function useOutdatedParentSyncNotification(user: any) {
  const { notifyWarning } = useGlobalNotifications();

  const districtId = idx(user, _ => parseInt(_.hierarchy.id));
  const { data } = useQuery<QueryResult, QueryVars>(GET_LAST_PARENT_SYNC, {
    variables: {
      input: {
        academic: {
          groupId: districtId!,
          groupType: 'district',
        },
      },
    },
    skip: !districtId,
    client: portalBFFClient,
  });
  const isLastSyncOlderThanSixMonths = React.useMemo(() => {
    if (!data || !data.jobs || data.jobs.length === 0) return false;
    const lastSuccessfulSync = data.jobs
      .filter(job => job.status === JobStatus.Success)
      .sort((a, b) => b.createdAt - a.createdAt)[0];
    if (!lastSuccessfulSync) return false;
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(
      sixMonthsAgo.getMonth() - SYNC_WARNING_THRESHOLD_MONTHS
    );
    const lastSyncDate = new Date(lastSuccessfulSync.createdAt);
    return lastSyncDate < sixMonthsAgo;
  }, [data]);
  const shownNotification = React.useRef(false);

  React.useEffect(() => {
    if (isLastSyncOlderThanSixMonths && !shownNotification.current) {
      if (!user || !user.permission || !districtId) return;
      const isAdmin = user.permission.adminLevel === 'district';
      const isApprover = user.permission.approverLevel === 'district';
      if (!isAdmin && !isApprover) return;
      shownNotification.current = true;
      notifyWarning({
        message: (
          <OutdatedParentSyncNotification
            isAdmin={isAdmin}
            isApprover={isApprover}
            districtId={districtId}
          />
        ),
      });
    }
  }, [isLastSyncOlderThanSixMonths]);
}

const OutdatedParentSyncNotification: React.FC<{
  isAdmin: boolean;
  isApprover: boolean;
  districtId: number;
}> = ({ isAdmin, isApprover, districtId }) => {
  return isAdmin ? (
    <>
      It’s been over 6 months since your last Parent List CSV sync. To ensure
      accurate delivery and engagement, please{' '}
      <a
        href={`${config.REACT_APP_PORTAL_BFF_URL}/mgmt/district/${districtId}/parents`}
      >
        upload an updated list
      </a>{' '}
      as soon as possible. For more instruction, view{' '}
      <a
        target="_blank"
        href="https://info.peachjar.com/manage-school-and-district-settings#SettingsforParents"
      >
        How to Manage Settings for Parents
      </a>
      .
    </>
  ) : isApprover ? (
    <>
      It’s been over 6 months since your last Parent List CSV sync. To ensure
      accurate delivery and engagement, please contact your admin to create an
      updated list as soon as possible.
    </>
  ) : (
    <></>
  );
};

const GET_LAST_PARENT_SYNC = gql`
  query GetLastParentSync($input: SisJobInput!) {
    jobs: getSisJobStatus(input: $input) {
      status
      createdAt
    }
  }
`;

interface QueryVars {
  input: {
    academic: {
      groupId: number;
      groupType: string;
    };
  };
}

enum JobStatus {
  Pending = 'pending',
  Processing = 'processing',
  Paused = 'paused',
  Cancelled = 'cancelled',
  Success = 'success',
  Failed = 'failed',
  InvalidFound = 'invalid',
}

type QueryResult = {
  jobs: Array<{
    status: JobStatus;
    createdAt: number;
  }>;
};
